// Der Spiegel: der Avatar-Editor als Dialog über dem Spiel (tasks/task-011,
// docs/architektur-paket-2.md, Abschnitt „Client“, `hud/`). Links dreht sich die eigene Figur
// langsam um sich selbst, rechts stehen die Farbfelder – acht bis zwölf je Bereich, keine
// Freitexteingabe. „Fertig“ schickt die Wahl mit `PUT /api/avatar` ans Konto.
//
// Beim ersten Anmelden geht der Spiegel von selbst auf und lässt sich nicht wegklicken: Die Wahl
// ist dort der Weg ins Spiel. Später öffnet ihn das Menü, und dann darf man ihn auch zumachen.
//
// Alles Entscheidbare liegt geprüft in `hud/spiegelZustand.ts`; hier steht nur die Ansicht – DOM
// und eine kleine eigene Three.js-Szene. Die zweite Szene hat ihren eigenen Renderer und ihr
// eigenes Canvas: Die Figur soll ruhig und formatfüllend im Dialog stehen, nicht im Spielbild
// herumfliegen. Sie entsteht erst beim ersten Öffnen, damit ein Spieler, der den Spiegel nie
// aufmacht, auch keinen zweiten WebGL-Kontext bezahlt.

import {
  Color,
  DirectionalLight,
  Group,
  HemisphereLight,
  PerspectiveCamera,
  Scene,
  WebGLRenderer,
} from 'three';
import { PLAYER, type AvatarColors } from '@bauernhof/shared';
import { createAvatarModel, type AvatarModel } from '../render/avatarModel.js';
import {
  beginneSpeichern,
  darfAbbrechen,
  felderFuer,
  speichernGeglueckt,
  speichernGescheitert,
  spiegelStart,
  waehleFarbe,
  type SpiegelZustand,
  type WahlBereich,
} from './spiegelZustand.js';

/** Maße und Bewegung der kleinen Szene im Dialog. */
const SICHT = {
  breite: 260,
  hoehe: 340,
  /** Volle Drehung in rund vierzehn Sekunden – langsam genug, um in Ruhe zu vergleichen. */
  drehung: 0.45,
  /** Blickfeld in Grad; eng, damit die Figur wenig perspektivisch verzerrt. */
  fov: 30,
  abstand: 4.0,
} as const;

export interface SpiegelOptionen {
  /**
   * Speichert die Farben am Konto. Liefert `null`, wenn es geklappt hat, sonst einen kurzen
   * deutschen Satz für den Spieler.
   */
  speichere(farben: AvatarColors): Promise<string | null>;
  /** Jede Wahl sofort: eigene Arme, eigene Figur – „Änderungen sind sofort sichtbar“. */
  beiVorschau(farben: AvatarColors): void;
  /** Der Spiegel ist zu; `gespeichert` sagt, ob die Wahl auch am Konto steht. */
  beiZu(farben: AvatarColors, gespeichert: boolean): void;
}

export interface Spiegel {
  /** Öffnet den Spiegel. `erstesMal` heißt: ohne Wahl geht er nicht wieder zu. */
  oeffne(farben: AvatarColors, erstesMal: boolean): void;
  istOffen(): boolean;
  /** Räumt Dialog, Renderer und Figur weg. */
  dispose(): void;
}

export function createSpiegel(parent: HTMLElement, optionen: SpiegelOptionen): Spiegel {
  let zustand: SpiegelZustand = spiegelStart({ haut: '#000000', haar: '#000000', oberteil: '#000000', hose: '#000000' }, false);
  let offen = false;
  let ansicht: Ansicht | null = null;
  let bild = 0;
  let letzteZeit: number | null = null;

  // --- Dialog ---------------------------------------------------------------------------------
  const wurzel = document.createElement('div');
  wurzel.className = 'spiegel';
  wurzel.hidden = true;

  const karte = document.createElement('div');
  karte.className = 'spiegel-karte';
  karte.setAttribute('role', 'dialog');
  karte.setAttribute('aria-modal', 'true');
  karte.setAttribute('aria-label', 'Spiegel: So siehst du aus');

  const titel = document.createElement('h1');
  titel.textContent = 'So siehst du aus';

  const koerper = document.createElement('div');
  koerper.className = 'spiegel-koerper';

  const buehne = document.createElement('div');
  buehne.className = 'spiegel-buehne';

  const wahl = document.createElement('div');
  wahl.className = 'spiegel-wahl';

  koerper.append(buehne, wahl);

  const fehler = document.createElement('p');
  fehler.className = 'spiegel-fehler';
  fehler.hidden = true;

  const knoepfe = document.createElement('div');
  knoepfe.className = 'spiegel-knoepfe';

  const fertig = document.createElement('button');
  fertig.type = 'button';
  fertig.className = 'spiegel-knopf';
  fertig.textContent = 'Fertig';
  fertig.addEventListener('click', () => void speichere());

  const abbrechen = document.createElement('button');
  abbrechen.type = 'button';
  abbrechen.className = 'spiegel-knopf spiegel-knopf-still';
  abbrechen.textContent = 'Abbrechen';
  abbrechen.addEventListener('click', () => schliesse(false));

  knoepfe.append(fertig, abbrechen);
  karte.append(titel, koerper, fehler, knoepfe);
  wurzel.append(karte);
  parent.append(wurzel);

  // Escape macht den Spiegel zu – aber nur, wenn er auch zugehen darf.
  wurzel.addEventListener('keydown', (ereignis) => {
    if (ereignis.key !== 'Escape') return;
    ereignis.stopPropagation();
    if (darfAbbrechen(zustand)) schliesse(false);
  });

  // --- Farbfelder -----------------------------------------------------------------------------
  /** Je Bereich und Farbe ein Knopf; sie bleiben stehen, nur die Markierung wandert. */
  const felder: { bereich: WahlBereich; farbe: string; knopf: HTMLButtonElement }[] = [];

  function baueWahl(): void {
    wahl.replaceChildren();
    felder.length = 0;
    for (const zeile of felderFuer(zustand.farben)) {
      const gruppe = document.createElement('div');
      gruppe.className = 'spiegel-gruppe';
      gruppe.setAttribute('role', 'group');
      gruppe.setAttribute('aria-label', zeile.titel);

      const name = document.createElement('span');
      name.className = 'spiegel-gruppentitel';
      name.textContent = zeile.titel;

      const reihe = document.createElement('div');
      reihe.className = 'spiegel-reihe';
      zeile.felder.forEach((feld, nummer) => {
        const knopf = document.createElement('button');
        knopf.type = 'button';
        knopf.className = 'spiegel-feld';
        knopf.style.background = feld.farbe;
        knopf.setAttribute('aria-label', `${zeile.titel}, Farbe ${nummer + 1}`);
        knopf.addEventListener('click', () => waehle(zeile.bereich, feld.farbe));
        felder.push({ bereich: zeile.bereich, farbe: feld.farbe, knopf });
        reihe.append(knopf);
      });

      gruppe.append(name, reihe);
      wahl.append(gruppe);
    }
  }

  // --- Ablauf ---------------------------------------------------------------------------------
  function waehle(bereich: WahlBereich, farbe: string): void {
    const neu = waehleFarbe(zustand, bereich, farbe);
    if (neu === zustand) return;
    zustand = neu;
    ansicht?.model.setFarben(zustand.farben);
    optionen.beiVorschau(zustand.farben);
    zeichneWahl();
  }

  async function speichere(): Promise<void> {
    if (zustand.stand === 'speichert') return;
    zustand = beginneSpeichern(zustand);
    zeichneWahl();
    const meldung = await optionen.speichere(zustand.farben);
    if (meldung === null) {
      zustand = speichernGeglueckt(zustand);
      schliesse(true);
      return;
    }
    zustand = speichernGescheitert(zustand, meldung);
    zeichneWahl();
  }

  function schliesse(gespeichert: boolean): void {
    if (!offen) return;
    offen = false;
    wurzel.hidden = true;
    if (bild !== 0) cancelAnimationFrame(bild);
    bild = 0;
    letzteZeit = null;
    optionen.beiZu(zustand.farben, gespeichert);
  }

  /** Markierung, Knöpfe und Fehlermeldung auf den Stand des Zustands bringen. */
  function zeichneWahl(): void {
    const speichert = zustand.stand === 'speichert';
    for (const { bereich, farbe, knopf } of felder) {
      const gewaehlt = zustand.farben[bereich] === farbe;
      knopf.classList.toggle('spiegel-feld-gewaehlt', gewaehlt);
      knopf.setAttribute('aria-pressed', gewaehlt ? 'true' : 'false');
      knopf.disabled = speichert;
    }
    fertig.disabled = speichert;
    fertig.textContent = speichert ? 'Speichert …' : 'Fertig';
    abbrechen.hidden = !darfAbbrechen(zustand);
    fehler.textContent = zustand.fehler ?? '';
    fehler.hidden = zustand.fehler === null;
  }

  // --- Die kleine Szene -----------------------------------------------------------------------
  interface Ansicht {
    renderer: WebGLRenderer;
    scene: Scene;
    camera: PerspectiveCamera;
    drehteller: Group;
    model: AvatarModel;
  }

  function baueAnsicht(farben: AvatarColors): Ansicht {
    const canvas = document.createElement('canvas');
    canvas.className = 'spiegel-canvas';
    buehne.append(canvas);

    const renderer = new WebGLRenderer({ canvas, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(SICHT.breite, SICHT.hoehe, false);

    const scene = new Scene();
    scene.background = new Color(0x1d1408);

    const camera = new PerspectiveCamera(SICHT.fov, SICHT.breite / SICHT.hoehe, 0.1, 20);
    // Auf halber Höhe der Figur, damit weder Kopf noch Füße perspektivisch wegkippen.
    camera.position.set(0, PLAYER.height / 2, SICHT.abstand);
    camera.lookAt(0, PLAYER.height / 2, 0);

    scene.add(new HemisphereLight(0xdfeeff, 0x6b5a3a, 1.0));
    const sonne = new DirectionalLight(0xfff1d6, 1.5);
    sonne.position.set(0.6, 1, 0.8);
    scene.add(sonne);

    const drehteller = new Group();
    scene.add(drehteller);
    const model = createAvatarModel(farben);
    drehteller.add(model.object);

    return { renderer, scene, camera, drehteller, model };
  }

  function frame(jetzt: number): void {
    if (!offen || ansicht === null) return;
    const dt = letzteZeit === null ? 0 : (jetzt - letzteZeit) / 1000;
    letzteZeit = jetzt;
    // Langsam um die eigene Achse; so sieht man Vorder- und Rückseite, ohne etwas bedienen zu müssen.
    ansicht.drehteller.rotation.y += SICHT.drehung * Math.min(dt, 0.1);
    ansicht.renderer.render(ansicht.scene, ansicht.camera);
    bild = requestAnimationFrame(frame);
  }

  return {
    oeffne(farben, erstesMal) {
      zustand = spiegelStart(farben, erstesMal);
      if (ansicht === null) ansicht = baueAnsicht(zustand.farben);
      else ansicht.model.setFarben(zustand.farben);
      // Beim ersten Anmelden zeigt der Spiegel von vorn; danach da, wo er stehen geblieben ist.
      ansicht.drehteller.rotation.y = 0;
      baueWahl();
      zeichneWahl();
      wurzel.hidden = false;
      offen = true;
      letzteZeit = null;
      bild = requestAnimationFrame(frame);
      fertig.focus();
    },

    istOffen: () => offen,

    dispose() {
      if (bild !== 0) cancelAnimationFrame(bild);
      bild = 0;
      offen = false;
      ansicht?.model.dispose();
      ansicht?.renderer.dispose();
      ansicht = null;
      wurzel.remove();
    },
  };
}
